import React, { useState, useEffect, useRef, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ThreeDots } from "react-loader-spinner";
import { FiX } from "react-icons/fi";
import PropTypes from "prop-types";
import { IoArrowDownOutline } from "react-icons/io5";

import { AiOutlineSend } from "react-icons/ai";
import {
  fetchUserChatHistory,
  AddChatMessageAction,
  sendChatMessage,
} from "../../Redux/actions/Crmactions/getChatWidget";
import { useSignalR } from "../../context/SignalRContext";

const PAGE_SIZE = 10;

const ChatMenu = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const chatState = useSelector((state) => state.ChatWidget || {});
  const { data: UserChat = [], loading = false, pagination: PaginationData = null } = chatState;

  const [newMessage, setNewMessage] = useState("");
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [aiAgentStatus, setAiAgentStatus] = useState("");

  const chatContainerRef = useRef(null);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const { connection } = useSignalR();

  // Track whether we need to restore scroll after prepending older messages
  const prevScrollHeightRef = useRef(null);

  const SuggestedQuestions = [
    "Show me recent contracts",
    "Total available units",
    "Total number of pending contracts",
  ];

  // Initial load when chat opens
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setHasMore(true);
      dispatch(fetchUserChatHistory(1, PAGE_SIZE));
    }
  }, [isOpen, dispatch]);

  // Update hasMore whenever pagination data changes
  useEffect(() => {
    if (PaginationData) {
      setHasMore(PaginationData.PageNumber < PaginationData.TotalPages);
    }
  }, [PaginationData]);

  // Listen for AI Agent Status updates via SignalR
  useEffect(() => {
    if (!connection) {
      // console.log("[ChatWidget] No connection available yet");
      return;
    }

    // console.log("[ChatWidget] Connection state:", connection.state);
    // console.log("[ChatWidget] SignalR connection established, setting up AIAgentStatus listener");

    // Remove any existing listener first to avoid duplicates
    connection.off("AIAgentStatus");

    const handleAIAgentStatus = (data) => {
      // console.log("[ChatWidget] AIAgentStatus received:", data);
      setAiAgentStatus(data);
    };

    // Add the listener
    connection.on("AIAgentStatus", handleAIAgentStatus);
    // console.log("[ChatWidget] AIAgentStatus listener registered");

    return () => {
      // console.log("[ChatWidget] Cleaning up AIAgentStatus listener");
      connection.off("AIAgentStatus", handleAIAgentStatus);
    };
  }, [connection]);

  // Auto-scroll to bottom on initial/new messages (not on page appends)
  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  // const scrollToBottom = () => {
  //   if (chatContainerRef.current) {
  //     chatContainerRef.current.scrollTo({
  //       top: chatContainerRef.current.scrollHeight,
  //       behavior: "smooth",
  //     });
  //   }
  // };

  useEffect(() => {
    // Only auto-scroll to bottom for page 1 load or new messages
    if (currentPage === 1) {
      scrollToBottom();
    }
  }, [UserChat, loadingMessage, currentPage]);

  // After prepending older messages, restore scroll position so user
  // stays at the same message they were reading (not jumping to top)
  useEffect(() => {
    if (prevScrollHeightRef.current !== null && chatContainerRef.current) {
      const newScrollHeight = chatContainerRef.current.scrollHeight;
      chatContainerRef.current.scrollTop = newScrollHeight - prevScrollHeightRef.current;
      prevScrollHeightRef.current = null;
    }
  }, [UserChat]);

  // Load next (older) page of messages
  const loadMoreMessages = async () => {
    if (paginationLoading || !hasMore) {
      return;
    }

    const nextPage = currentPage + 1;

    // Save scroll height BEFORE prepend so we can restore position after
    if (chatContainerRef.current) {
      prevScrollHeightRef.current = chatContainerRef.current.scrollHeight;
    }

    setPaginationLoading(true);
    setCurrentPage(nextPage);
    await dispatch(fetchUserChatHistory(nextPage, PAGE_SIZE));
    setPaginationLoading(false);
  };

  // Handle sending messages
  const handleSendMessage = async (text) => {
    const messageToSend = newMessage || text;
    if (!messageToSend?.trim()) {
      return;
    }

    const userMsg = {
      Id: `${Math.random()}`,
      Question: messageToSend,
    };

    // console.log("[ChatWidget] Sending message:", messageToSend);
    dispatch(AddChatMessageAction(userMsg));
    setNewMessage("");
    setLoadingMessage(true);
    setAiAgentStatus(""); // Clear previous status
    setShowSuggestions(false);

    try {
      // console.log("[ChatWidget] Waiting for AI response...");
      const response = await sendChatMessage(messageToSend, "ERP");
      // console.log("[ChatWidget] AI response received:", response);

      if (response?.data?.answer) {
        dispatch(
          AddChatMessageAction({
            Id: `${Math.random()}`,
            Answer: response.data.answer,
          }),
        );
      }
      // Refresh from page 1 to get the latest bot reply
      // setCurrentPage(1);
      // setHasMore(true);
      // await dispatch(fetchUserChatHistory(1, PAGE_SIZE));
    } catch (err) {
      console.error("[ChatWidget] Error sending message:", err);
      dispatch(
        AddChatMessageAction({
          Id: `${Math.random()}`,
          Answer: "Something went wrong. Try again.",
        }),
      );
    } finally {
      setLoadingMessage(false);
      setAiAgentStatus("");
      scrollToBottom();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Scroll handler — show/hide "go to bottom" button + trigger pagination at top
  useEffect(() => {
    const handleScroll = () => {
      const el = chatContainerRef.current;
      if (!el) {
        return;
      }

      // Show scroll-to-bottom button when far from bottom
      setShowScrollButton(el.scrollTop + el.clientHeight < el.scrollHeight - 100);

      // When scrolled near the top (within 50px), load more older messages
      if (el.scrollTop <= 50 && hasMore && !paginationLoading) {
        loadMoreMessages();
      }
    };

    const el = chatContainerRef.current;
    el?.addEventListener("scroll", handleScroll);
    return () => el?.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, paginationLoading, currentPage]);

  return (
    <div className={`chat-widget-container ${isOpen ? "open" : ""}`}>
      <div className="chat-widget-wrapper">
        {/* Header */}
        <div className="chat-header">
          <h6 className="chat-title">AI Agent</h6>
          <button className="chat-close-btn" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>

        {/* Messages Container */}
        <div className="chat-messages" ref={chatContainerRef}>
          {/* Pagination loader at top */}
          {paginationLoading && (
            <div className="chat-pagination-loader">
              <ThreeDots height="25" width="35" color="#5CD1D0" />
            </div>
          )}

          {/* "No more messages" indicator */}
          {/* {!paginationLoading && !hasMore && UserChat.length > 0 && (
            <div className="chat-no-more">
              <span>No more messages</span>
            </div>
          )} */}

          {/* Initial loading */}
          {loading && UserChat.length === 0 && (
            <div className="chat-loading-container">
              <ThreeDots height="40" width="40" color="#5CD1D0" />
              <p className="fz-12">Loading...</p>
            </div>
          )}

          {/* Empty state */}
          {!loading && UserChat?.length === 0 && (
            <div className="chat-empty-container">
              <p className="fz-12">Start a conversation with AI Agent</p>
              <img
                src="/chat-bot.jpg"
                alt="AI Bot"
                className="chat-empty-img"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>
          )}

          {/* Messages list */}
          {UserChat?.length > 0 &&
            UserChat?.map((item) => (
              <Fragment key={item.Id}>
                {/* User Question */}
                {item.Question && (
                  <div className="chat-message-row user-message-row">
                    <div className="chat-message user-message">
                      <p className="fz-14">{item.Question}</p>
                    </div>
                  </div>
                )}

                {/* Bot Answer */}
                {item.Answer && (
                  <div className="chat-message-row bot-message-row">
                    <div className="chat-message bot-message">
                      <p className="fz-14">{item?.Answer}</p>
                    </div>
                  </div>
                )}
              </Fragment>
            ))}

          {/* Bot typing indicator */}
          {loadingMessage && (
            <div className="chat-message-row bot-message-row">
              <div className="chat-message bot-message">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <ThreeDots height="20" width="30" color="#5CD1D0" />
                  {aiAgentStatus && (
                    <p className="fz-11" style={{ opacity: 0.8, margin: 0 }}>
                      {aiAgentStatus}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Scroll to Bottom Button */}
          {showScrollButton && (
            <button className="chat-scroll-btn" onClick={scrollToBottom}>
              <IoArrowDownOutline size={20} />
            </button>
          )}

          {/* Suggested Questions */}
          {showSuggestions && SuggestedQuestions?.length > 0 && (
            <div className="chat-suggestions">
              {SuggestedQuestions.map((q, i) => (
                <button
                  key={i}
                  className="chat-suggestion-btn"
                  onClick={() => handleSendMessage(q)}
                >
                  <p className="fz-11">{q}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="chat-input-container">
          <div className="chat-input-row">
            <textarea
              className="chat-input"
              placeholder="Type your message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loadingMessage}
              rows="1"
            />
            <button
              className="chat-send-btn"
              onClick={() => handleSendMessage()}
              disabled={loadingMessage || !newMessage.trim()}
            >
              <AiOutlineSend size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

ChatMenu.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ChatMenu;
